/* eslint-disable no-nested-ternary */
import React, {
  useContext, useEffect, useState,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { Button, Modal, Box } from '@mui/material';
import PropTypes from 'prop-types';
import { Navbar, Sidebar } from '../../components';
import { sendRequest } from '../../api/shootAPI';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import API from '../../api/endpoints';
import DateRangePicker from '../../components/dateRangePicker';
import IconHelp from '../../images/icon-help.svg';
import IconFormatList from '../../images/icon-format-list.svg';
import IconClose from '../../images/icon-close.svg';
// import NewIcon from '../../images/icon-in-new-green.svg';
import './analytics.css';
import MemberSelect from '../userDashboard/MemberSelect';
import { UnusualActivityTypes } from '../../constants/unusualActivityTypes';
import TimezoneSelectWrapper from '../../components/selectors/TimezoneSelectWrapper';
import CustomTable from '../../components/table/CustomTable';

const Avatar = styled.figure`
  margin: 0;
  padding: 8px 6px;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  color: var(--Blue-Blue-01, #213D69);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 125%; /* 17.5px */
  background: var(--Blue-Blue-07, #D8E6FC);
  text-align: center;
  ${(props) => props.src && `
    background-size: cover;
    background-image: url(${props.src});
  `}
`;

const TABLE_HEADERS = [
  {
    label: 'Date & Time',
    sortable: false,
    colSpan: 2,
  },
  {
    label: 'Duration',
    sortable: false,
    colSpan: 1,
  },
  {
    label: 'Type',
    sortable: false,
    colSpan: 1.5,
  },
  {
    label: 'Confidence',
    sortable: false,
    colSpan: 1,
  },
  {
    label: 'Overall Activity',
    sortable: false,
    colSpan: 1,
  },
  {
    label: 'Action',
    sortable: false,
    colSpan: 1,
  },
];

const CustomTableStyles = {
  container: {
    marginTop: 0,
  },
  header: {
    backgroundColor: 'var(--dark-10)',
    fontSize: '14px',
    borderBottom: '1px solid var(--Dark-Dark-06, #E0E1E6) !important',
    borderRadius: '0 !important',
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fff !important',
    },
    '&:hover': {
      backgroundColor: 'var(--dark-10) !important',
    },
  },
  cell: {
    button: {
      whiteSpace: 'nowrap',
      color: 'var(--dark-2)',
      borderColor: 'var(--dark-4) !important',
    },
  },
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 946,
  bgcolor: '#FFF',
  borderRadius: '8px',
  boxShadow: 24,
  p: 4,
};

function Card({ title, description, subTitle = '' }) {
  return (
    <div className="card-wrapper">
      <div>
        <div className="help-text text-600">
          {title}
        </div>
        {
            subTitle ? (
              <div className="mt-1 card-sub-title">{subTitle}</div>
            ) : ''
        }
      </div>
      <div className="card-text">
        {description}
      </div>
    </div>
  );
}

Card.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  subTitle: PropTypes.string,
};

function UnusualActivityMember() {
  const { memberId } = useParams();
  const { activeWorkspace } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [member, setMember] = useState(null);
  const [instances, setInstances] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedTimezone, setSelectedTimezone] = useState(activeWorkspace.timezone);
  const [date, setDate] = useState(() => {
    const initialToday = moment.tz(selectedTimezone).toDate();
    return [moment.tz(selectedTimezone).subtract(6, 'days').toDate(), initialToday];
  });
  const [summary, setSummary] = useState({
    totalMembers: 0, totalInstances: 0, totalTime: 0,
  });
  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const [openModal, setOpenModal] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(0);

  const selectedInstanceData = (instances && instances[selectedInstance]) || {};
  const startDate = moment(selectedInstanceData.timeSlot || selectedInstanceData.start);
  const endDate = selectedInstanceData.end
    ? moment(selectedInstanceData.end)
    : startDate.isValid() ? startDate.clone().add(selectedInstanceData.duration || 0, 'seconds') : moment();
  const duration = selectedInstanceData.duration || 0;
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);

  const fetchMemberActivity = async () => {
    setIsLoading(true);

    // Convert dates to UTC using selected timezone
    const startOfDay = moment.tz(date[0], selectedTimezone)
      .startOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const endOfDay = moment.tz(date[1], selectedTimezone)
      .endOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const { success, data: res } = await sendRequest({
      method: 'get',
      url: API.UNUSUAL_ACTIVITY.getMemberDetails(memberId),
      params: {
        workspaceId: activeWorkspace.id,
        startDate: startOfDay,
        endDate: endOfDay,
        timezone: selectedTimezone,
      },
      timeout: 30000,
    });

    const { data } = res ?? { success: false, data: { members: [] } };

    if (success) {
      setMember(data.member);
      setInstances(data.instances);
      setSummary(data.summary);
    } else {
      displayNotification({
        message: 'There was an issue fetching member unusual activity. Please try again',
        severity: 'error',
      });
    }
    setIsLoading(false);
  };

  const fetchMembers = async () => {
    // Convert dates to UTC using selected timezone
    const startOfDay = moment.tz(date[0], selectedTimezone)
      .startOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const endOfDay = moment.tz(date[1], selectedTimezone)
      .endOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const { success, data: res } = await sendRequest({
      method: 'get',
      url: API.UNUSUAL_ACTIVITY.getWorkspaceSummary(activeWorkspace.id),
      params: {
        startDate: startOfDay,
        endDate: endOfDay,
        timezone: selectedTimezone,
      },
      timeout: 30000,
    });

    if (success && res?.data?.members) {
      setMembers(res.data.members);
    }
  };

  useEffect(() => {
    if (activeWorkspace?.id) {
      fetchMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, activeWorkspace, selectedTimezone]);

  useEffect(() => {
    if (memberId && activeWorkspace?.id) {
      fetchMemberActivity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, date, activeWorkspace, selectedTimezone]);

  // const handleOpen = () => {
  //   setOpenModal(true);
  // };

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleViewInstance = (index) => {
    setSelectedInstance(index);
    setOpenModal(true);
  };

  // Prepare table data for instances
  const instancesTableData = (instances || []).map((instance, index) => {
    const instanceStart = moment(instance.start || instance.timeSlot);
    const instanceEnd = instance.end
      ? moment(instance.end)
      : instanceStart.clone().add(instance.duration || 0, 'seconds');
    const instanceDuration = instance.duration || 0;
    const instanceHours = Math.floor(instanceDuration / 3600);
    const instanceMinutes = Math.floor((instanceDuration % 3600) / 60);

    return {
      dateTime: `${instanceStart.format('ddd, MMM D, YYYY h:mm a')} - ${instanceEnd.format('h:mm a')}`,
      duration: `${String(instanceHours).padStart(2, '0')}:${String(instanceMinutes).padStart(2, '0')}`,
      type: UnusualActivityTypes[instance.unusualTypeId]?.title || 'Unknown',
      confidence: (
        <div className={`table-confidence-${instance.confidence?.toLowerCase() || 'low'}`}>
          {instance.confidence || 'Low'}
        </div>
      ),
      overallActivity: `${instance.overallActivity || 0}%`,
      action: (
        <Button
          variant="outlined"
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => handleViewInstance(index)}
        >
          View
        </Button>
      ),
    };
  });

  return (
    <>
      <Navbar />
      <Sidebar />
      <Modal
        open={openModal}
        onClose={handleClose}
      >
        <Box sx={{ ...style }} className="d-flex flex-column gap-3">
          <div className="modal-header-text d-flex flex-row justify-content-between align-items-center">
            <div className="modal-header-text">
              {startDate.isValid() ? startDate.format('ddd, D MMM YYYY') : ''}
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="cursor-pointer"
              aria-label="Close"
              style={{
                background: 'none', border: 'none', padding: 0, margin: 0, lineHeight: 0,
              }}
            >
              <img
                src={IconClose}
                width={20}
                height={20}
                alt="close"
                style={{ display: 'block' }}
              />
            </button>
          </div>
          <div className="d-flex flex-row justify-content-between align-items-center">
            {member && (
              <div className="d-flex gap-2 align-items-center">
                <Avatar src={member?.user?.profilePicture}>
                  {
                    !member?.user?.profilePicture && (
                      (member?.user?.firstName && member?.user?.lastName
                      && `${member?.user?.firstName?.slice(0, 1)}${member?.user?.lastName?.slice(0, 1)}`)
                    )
                  }
                </Avatar>
                <div className="d-flex gap-1">
                  <div className="modal-member-name-text text-600">
                    {`${member?.user?.firstName} ${member?.user?.lastName}`}
                  </div>
                  <div className="modal-member-name-text text-600">|</div>
                  <div className="modal-member-name-text text-400">
                    {selectedInstanceData?.detectionTypes?.length > 1
                      ? `${selectedInstanceData.detectionTypes.length} Detection Types`
                      : '1 Detection Type'}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <div className="d-flex flex-column gap-4 w-100">
              {/* Show all detection types when multiple are present */}
              {selectedInstanceData?.detectionTypes && selectedInstanceData.detectionTypes.length > 1 ? (
                <div className="d-flex flex-column gap-3">
                  <div className="modal-member-name-text text-600">
                    Multiple Detection Types
                  </div>
                  {selectedInstanceData.detectionTypes.map((type) => (
                    <div key={type} className="d-flex flex-column gap-2">
                      <div className="d-flex modal-member-name-text text-600" style={{ fontSize: '14px' }}>
                        •
                        {' '}
                        {UnusualActivityTypes[type]?.title || type}
                      </div>
                      <div className="help-text" style={{ marginLeft: '12px' }}>
                        {selectedInstanceData.detectionData?.[type]?.message
                          || UnusualActivityTypes[type]?.description
                          || ''}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex modal-member-name-text text-600">
                    {selectedInstanceData?.unusualTypeId !== undefined
                      ? UnusualActivityTypes[selectedInstanceData.unusualTypeId]?.title
                      : 'Unknown Activity'}
                  </div>
                  <div className="help-text">
                    {selectedInstanceData?.detectionData?.[selectedInstanceData?.unusualTypeId]?.message
                      || selectedInstanceData?.detectionData?.message
                      || UnusualActivityTypes[selectedInstanceData?.unusualTypeId]?.description
                      || ''}
                  </div>
                </div>
              )}
              <div className="d-flex justify-content-between">
                <div className="d-flex flex-column gap-2">
                  <div className="help-text text-500">Date & Time</div>
                  <div className="unusual-activity-modal-content-text">
                    {`${startDate.format('ddd, MMM D, YYYY h:mm a')} - \
                    ${endDate.format('h:mm a')} (
                    ${String(hours).padStart(2, '0')}:
                    ${String(minutes).padStart(2, '0')})`}
                  </div>
                </div>
                <div className="d-flex flex-column gap-2">
                  <div className="help-text text-500">Duration</div>
                  <div className="unusual-activity-modal-content-text">
                    {`${hours}hr ${minutes}m`}
                  </div>
                </div>
              </div>
              {/* Additional details for the selected instance can be added here */}
            </div>
          </div>
        </Box>
      </Modal>

      <div className="screen-wrapper">
        <div className="screen-header">
          <div className="screen-header-container d-flex justify-content-between position-relative">
            <div className="w-100 d-flex justify-content-between align-center">
              <div className="header-manage-member w-auto text-600">
                Unusual Activity
              </div>
              <div className="d-flex gap-4">
                <div className="d-flex align-items-center">
                  <img src={IconHelp} width={20} height={20} alt="help" />
                  <div className="help-text">
                    About Unusual Activity
                  </div>
                </div>
                <Button className="smart-notification-btn">
                  <img src={IconFormatList} alt="list" />
                </Button>
              </div>
            </div>
          </div>
          <div className="d-flex gap-3 mb-3">
            <div className="team-select-wrapper">
              <MemberSelect
                menuItems={members}
                value={+memberId}
                handleForm={(e) => history.push(`/analytics/unusual-activity/${e.target.value}`)}
              />
            </div>
            <div className="date-picker-wrapper">
              <DateRangePicker
                value={date}
                onChange={(_date) => setDate(_date)}
              />
            </div>
            <div className="timezone-select-wrapper">
              <TimezoneSelectWrapper
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
              />
            </div>
          </div>
          <div className="d-flex gap-3 justify-space-between">
            <Card
              title="Instances"
              description={summary.totalInstances}
              subTitle="Total unusual activities"
            />
            <Card
              title="Total Time (hh:mm:ss)"
              description={moment.utc(summary.totalTime * 1000).format('HH:mm:ss')}
            />
            <Card
              title="Previous 60 days (hh:mm:ss)"
              description="00:00:00"
              subTitle="Historical comparison"
            />
          </div>
          <div>
            <div className="d-flex gap-2 mt-3 mb-3">
              <div className="analysis-title">
                Instances
              </div>
            </div>
            <CustomTable
              headers={TABLE_HEADERS}
              items={instancesTableData}
              styles={CustomTableStyles}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default UnusualActivityMember;
